import type { ReactNode } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Navigate, useParams } from "@tanstack/react-router";
import { useOrganizerClubs } from "@/lib/clubs/queries";
import { ClubsEmptyState } from "./ClubsEmptyState";
import styles from "./ClubGuard.module.scss";

interface Props {
  /** When set, gates access to a specific :id; otherwise just gates the shell. */
  requireClubId?: boolean;
  children: ReactNode;
}

/**
 * Gate a `/clubs/...` subtree on organizer status.
 *
 * - Unauthenticated → redirect to `/sign-in` (hard reject is fine here).
 * - Signed-in but organizes nothing (Plotlounge-only or member-only) →
 *   render the soft-reject empty state. We deliberately don't 403/404
 *   because the cockpit is a public-facing entry point — landing on
 *   "you don't run any clubs yet" is friendlier and easier to recover from.
 * - Organizes at least one club but URL `:id` isn't in their set → soft reject.
 */
export function ClubGuard({ requireClubId = false, children }: Props) {
  const { isLoaded: clerkLoaded, isSignedIn } = useAuth();
  const {
    data: clubs,
    isLoading,
    isError,
    error,
    refetch,
  } = useOrganizerClubs();
  const params = useParams({ strict: false }) as { id?: string };

  if (!clerkLoaded) return <Loading />;
  if (!isSignedIn) return <Navigate to="/sign-in" />;
  if (isLoading) return <Loading />;
  if (isError || !clubs) {
    return (
      <ClubsEmptyState
        reason="error"
        error={error}
        onRetry={() => void refetch()}
      />
    );
  }

  if (clubs.length === 0) {
    return <ClubsEmptyState reason="not-organizer" />;
  }

  if (requireClubId) {
    const id = params.id;
    if (!id || !clubs.some((c) => c.id === id)) {
      return <ClubsEmptyState reason="not-organizer-of-club" />;
    }
  }

  return <>{children}</>;
}

function Loading() {
  return (
    <div className={styles.loading} role="status" aria-live="polite">
      Loading…
    </div>
  );
}
